// Biblioteca de modelos pré-prontos de proposta (catálogo estático, não persistido).
// Cada item segue o mesmo shape de um PropostasTemplates do backend, exceto por não ter
// _id/cliente_id — para usar em uma proposta real, o front aplica o conteúdo diretamente
// (PropostaEditor) ou, se o usuário for admin, salva uma cópia via criarTemplate.
//
// Todos os modelos nascem no modo Proposta (canvas) com o layout rico montado por
// documentoIaParaCanvas (hero escuro, chips numerados, destaque, fundo em tom suave da
// cor do tema, rodapé com paginação); os `blocos` ficam como fallback para quem trocar
// para o modo Orçamento.

import { documentoIaParaCanvas, fundoComoRetangulo } from './editor/canvas/canvasUtils';

export const CATEGORIAS_MODELO = [
  { value: 'geral', label: 'Geral' },
  { value: 'consultoria', label: 'Consultoria' },
  { value: 'tecnologia', label: 'Tecnologia & Software' },
  { value: 'design', label: 'Design & Branding' },
  { value: 'marketing', label: 'Marketing Digital' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'arquitetura', label: 'Arquitetura & Interiores' },
  { value: 'construcao', label: 'Construção & Reforma' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'saude', label: 'Saúde & Estética' },
  { value: 'educacao', label: 'Educação & Cursos' },
  { value: 'imobiliaria', label: 'Imobiliária' },
  { value: 'servicos_tecnicos', label: 'Serviços Técnicos' },
];

export function getCategoriaLabel(value) {
  return CATEGORIAS_MODELO.find(c => c.value === value)?.label || 'Geral';
}

function blocosPadrao({ apresentacao, extra, formaPagamento, observacoes }) {
  const blocos = [
    { tipo: 'data', titulo: '', conteudo_html: '', config: { cidade: '' }, ordem: 0 },
    { tipo: 'dados_cliente', titulo: '', conteudo_html: '', config: {}, ordem: 1 },
    { tipo: 'texto', titulo: 'Apresentação', conteudo_html: apresentacao, config: {}, ordem: 2 },
  ];
  let ordem = 3;
  if (extra) {
    blocos.push({ tipo: 'texto', titulo: extra.titulo, conteudo_html: extra.conteudo_html, config: {}, ordem: ordem++ });
  }
  blocos.push({ tipo: 'itens', titulo: '', conteudo_html: '', config: {}, ordem: ordem++ });
  blocos.push({ tipo: 'forma_pagamento', titulo: 'Forma de pagamento', conteudo_html: formaPagamento, config: {}, ordem: ordem++ });
  blocos.push({ tipo: 'validade', titulo: '', conteudo_html: '', config: {}, ordem: ordem++ });
  blocos.push({ tipo: 'observacoes', titulo: 'Observações', conteudo_html: observacoes, config: {}, ordem: ordem++ });
  return blocos;
}

// Tom bem claro da cor do tema, usado como fundo das páginas do canvas — dá
// identidade ao modelo sem comprometer a leitura (texto do documento é escuro).
function tintHex(hex, intensidade = 0.955) {
  const n = String(hex || '#7C3AED').replace('#', '');
  const full = n.length === 3 ? n.split('').map(c => c + c).join('') : n;
  const canal = (i) => Math.round(parseInt(full.slice(i, i + 2), 16) * (1 - intensidade) + 255 * intensidade);
  return `#${[canal(0), canal(2), canal(4)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

// O editor (Tailwind preflight) zera list-style de <ul>/<li>; no canvas as listas
// viram parágrafos com "•", que renderizam igual no editor, no PDF e no link público.
function listaParaParagrafos(html) {
  return String(html || '')
    .replace(/<\/?(ul|ol)>/g, '')
    .replace(/<li>/g, '<p>• ')
    .replace(/<\/li>/g, '</p>');
}

// {{lead.nome}} não é variável suportada no canvas (só contato.*/proposta.*/empresa.*)
// — troca por um genérico que funciona em qualquer proposta.
function semVariavelLead(html) {
  return String(html || '').replace(/\{\{\s*lead\.nome\s*\}\}/g, 'o seu projeto');
}

// Monta o canvas rico do modelo a partir do conteúdo textual (mesmo builder usado
// pela geração com IA — mantém os modelos e o agente com a mesma cara).
function montarCanvasModelo(modelo) {
  const c = modelo.conteudo;
  const secoes = [{ tipo: 'texto', titulo: '1. Apresentação', html: semVariavelLead(c.apresentacao) }];
  let n = 2;
  if (c.extra) {
    secoes.push({ tipo: 'texto', titulo: `${n++}. ${c.extra.titulo}`, html: listaParaParagrafos(semVariavelLead(c.extra.conteudo_html)) });
  }
  secoes.push({ tipo: 'itens', titulo: `${n++}. Investimento` });
  if (c.formaPagamento) {
    secoes.push({ tipo: 'texto', titulo: `${n++}. Forma de Pagamento`, html: c.formaPagamento });
  }
  if (c.observacoes) {
    secoes.push({ tipo: 'destaque', html: c.observacoes });
  }
  const canvas = documentoIaParaCanvas({ secoes }, {
    titulo: modelo.nome,
    temaCor: modelo.layout?.tema_cor,
    estilo: modelo.design_estilo,
    compacto: true,
  });
  // Estilos "limpos" pedem fundo branco; os demais usam o tom suave do tema — e esse
  // fundo entra como CAMADA de retângulo (editável no painel de camadas), não como
  // cor da folha.
  const fundo = ['minimal', 'central', 'geometrico'].includes(modelo.design_estilo)
    ? null
    : tintHex(modelo.layout?.tema_cor);
  return fundo ? fundoComoRetangulo(canvas, fundo) : canvas;
}

const BASE_MODELOS = [
  {
    id: 'consultoria-empresarial',
    design_estilo: 'hero',
    nome: 'Consultoria Empresarial',
    descricao: 'Diagnóstico, plano de ação e acompanhamento de indicadores para projetos de consultoria.',
    categoria: 'consultoria',
    layout: { tema_cor: '#1E3A8A', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Ficamos à disposição para esclarecer qualquer dúvida sobre esta proposta.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, é um prazer apresentar esta proposta de consultoria para {{lead.nome}}. Nosso objetivo é diagnosticar os principais gargalos de gestão e implementar um plano de ação orientado a resultados mensuráveis.</p>',
      extra: { titulo: 'Metodologia', conteudo_html: '<p>Trabalhamos em 3 etapas: <strong>Diagnóstico</strong>, <strong>Plano de Ação</strong> e <strong>Acompanhamento de Resultados</strong>, com reuniões quinzenais e relatórios de indicadores.</p>' },
      formaPagamento: '<p>50% na assinatura do contrato e 50% na entrega do plano de ação. Aceitamos PIX, transferência bancária ou boleto.</p>',
      observacoes: '<p>Os prazos podem ser ajustados conforme a disponibilidade das áreas envolvidas no diagnóstico.</p>'
    },
    itens_padrao: [
      { descricao: 'Diagnóstico organizacional', quantidade: 1, preco_unitario: 3500, desconto_percent: 0 },
      { descricao: 'Plano de ação estratégico (3 meses)', quantidade: 1, preco_unitario: 8000, desconto_percent: 0 },
      { descricao: 'Acompanhamento mensal de indicadores', quantidade: 3, preco_unitario: 1500, desconto_percent: 0 },
    ],
    validade_dias_default: 15
  },
  {
    id: 'desenvolvimento-software',
    design_estilo: 'blocado',
    nome: 'Desenvolvimento de Software',
    descricao: 'Escopo técnico, sprints e suporte pós-entrega para projetos de tecnologia.',
    categoria: 'tecnologia',
    layout: { tema_cor: '#7C3AED', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Dúvidas técnicas? Fale com nosso time de engenharia.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, segue nossa proposta técnica e comercial para o desenvolvimento de {{lead.nome}}. Nossa equipe combina engenharia de software e produto para entregar um sistema robusto, escalável e dentro do prazo.</p>',
      extra: { titulo: 'Escopo técnico', conteudo_html: '<ul><li>Levantamento de requisitos e arquitetura</li><li>Desenvolvimento front-end e back-end</li><li>Integrações com APIs de terceiros</li><li>Testes, homologação, deploy e suporte pós-entrega</li></ul>' },
      formaPagamento: '<p>Pagamento por sprint entregue, faturado quinzenalmente via boleto ou PIX. Código-fonte e documentação entregues ao final de cada sprint.</p>',
      observacoes: '<p>Escopo pode ser ajustado via change request, com impacto de prazo e valor comunicado antes da execução.</p>'
    },
    itens_padrao: [
      { descricao: 'Levantamento de requisitos e arquitetura', quantidade: 1, preco_unitario: 4000, desconto_percent: 0 },
      { descricao: 'Desenvolvimento (sprint de 2 semanas)', quantidade: 4, preco_unitario: 6000, desconto_percent: 0 },
      { descricao: 'Suporte pós-entrega (mensal)', quantidade: 1, preco_unitario: 1200, desconto_percent: 0 },
    ],
    validade_dias_default: 10
  },
  {
    id: 'design-branding',
    design_estilo: 'minimal',
    nome: 'Design & Branding',
    descricao: 'Identidade visual completa: pesquisa, conceitos, manual de marca e arquivos finais.',
    categoria: 'design',
    layout: { tema_cor: '#DB2777', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Vamos criar uma marca que representa exatamente quem vocês são.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}! Preparamos esta proposta criativa para dar vida à identidade visual de {{lead.nome}}, com uma marca que comunica exatamente quem vocês são.</p>',
      extra: { titulo: 'O que está incluso', conteudo_html: '<ul><li>Pesquisa de mercado e concorrentes</li><li>Criação de 3 conceitos de logotipo</li><li>Paleta de cores e tipografia</li><li>Manual de identidade visual (brandbook)</li><li>Arquivos em alta resolução e vetor</li></ul>' },
      formaPagamento: '<p>40% no início do projeto e 60% na entrega final dos arquivos e brandbook.</p>',
      observacoes: '<p>Inclui até 2 rodadas de ajustes por etapa. Rodadas adicionais são orçadas à parte.</p>'
    },
    itens_padrao: [
      { descricao: 'Pesquisa e briefing criativo', quantidade: 1, preco_unitario: 800, desconto_percent: 0 },
      { descricao: 'Criação de identidade visual (3 conceitos)', quantidade: 1, preco_unitario: 3200, desconto_percent: 0 },
      { descricao: 'Manual de marca (brandbook)', quantidade: 1, preco_unitario: 1500, desconto_percent: 0 },
    ],
    validade_dias_default: 10
  },
  {
    id: 'marketing-digital',
    design_estilo: 'faixa',
    nome: 'Marketing Digital',
    descricao: 'Gestão de tráfego pago, produção de conteúdo e relatórios de performance.',
    categoria: 'marketing',
    layout: { tema_cor: '#F97316', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Estratégia orientada a dados, com otimização contínua.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, montamos esta proposta de marketing digital para acelerar os resultados de {{lead.nome}} nos canais online, com estratégia orientada a dados.</p>',
      extra: { titulo: 'Estratégia incluída', conteudo_html: '<ul><li>Gestão de tráfego pago (Meta Ads e Google Ads)</li><li>Produção de conteúdo para redes sociais</li><li>Relatórios mensais de performance</li><li>Otimização contínua de campanhas</li></ul>' },
      formaPagamento: '<p>Mensalidade recorrente, cobrada todo dia 5, via cartão de crédito, PIX ou boleto. Verba de mídia é gerenciada à parte, direto na plataforma de anúncios.</p>',
      observacoes: '<p>Resultados de tráfego pago começam a aparecer a partir da 2ª/3ª semana de otimização das campanhas.</p>'
    },
    itens_padrao: [
      { descricao: 'Gestão de tráfego pago (mensal)', quantidade: 1, preco_unitario: 2500, desconto_percent: 0 },
      { descricao: 'Produção de conteúdo (12 posts/mês)', quantidade: 1, preco_unitario: 1800, desconto_percent: 0 },
      { descricao: 'Relatório de performance mensal', quantidade: 1, preco_unitario: 0, desconto_percent: 0 },
    ],
    validade_dias_default: 7
  },
  {
    id: 'juridico',
    design_estilo: 'minimal',
    nome: 'Serviços Jurídicos',
    descricao: 'Honorários e escopo de atuação para acompanhamento processual ou consultivo.',
    categoria: 'juridico',
    layout: { tema_cor: '#111827', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Atuação com o compromisso técnico e a discrição que a advocacia exige.' },
    conteudo: {
      apresentacao: '<p>Prezado(a) {{contato.nome}}, apresentamos nossos honorários e escopo de atuação para o caso de {{lead.nome}}.</p>',
      extra: { titulo: 'Escopo da atuação', conteudo_html: '<p>A atuação contempla análise documental, elaboração de peças processuais, acompanhamento de audiências e atualização periódica sobre o andamento processual.</p>' },
      formaPagamento: '<p>Honorários fixos definidos nesta proposta, podendo incluir êxito conforme acordado em contrato de honorários específico.</p>',
      observacoes: '<p>Custas processuais e despesas cartorárias não estão incluídas nos valores acima.</p>'
    },
    itens_padrao: [
      { descricao: 'Análise inicial e parecer jurídico', quantidade: 1, preco_unitario: 1500, desconto_percent: 0 },
      { descricao: 'Honorários advocatícios (fase processual)', quantidade: 1, preco_unitario: 6000, desconto_percent: 0 },
      { descricao: 'Acompanhamento processual (mensal)', quantidade: 1, preco_unitario: 500, desconto_percent: 0 },
    ],
    validade_dias_default: 15
  },
  {
    id: 'arquitetura-interiores',
    design_estilo: 'central',
    nome: 'Arquitetura & Interiores',
    descricao: 'Estudo preliminar, projeto executivo e acompanhamento de obra.',
    categoria: 'arquitetura',
    layout: { tema_cor: '#92400E', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Unindo estética, funcionalidade e o seu estilo de vida.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, é com entusiasmo que apresentamos esta proposta de projeto arquitetônico/interiores para {{lead.nome}}.</p>',
      extra: { titulo: 'Etapas do projeto', conteudo_html: '<ul><li>Estudo preliminar e briefing</li><li>Anteprojeto com plantas e 3D</li><li>Projeto executivo detalhado</li><li>Acompanhamento de obra (opcional)</li></ul>' },
      formaPagamento: '<p>Pagamento dividido em 3 parcelas: assinatura do contrato, entrega do anteprojeto e entrega do projeto executivo.</p>',
      observacoes: '<p>Renders 3D e memorial descritivo inclusos. Móveis e decoração sob consulta separada.</p>'
    },
    itens_padrao: [
      { descricao: 'Estudo preliminar', quantidade: 1, preco_unitario: 2000, desconto_percent: 0 },
      { descricao: 'Projeto executivo completo', quantidade: 1, preco_unitario: 9000, desconto_percent: 0 },
      { descricao: 'Acompanhamento de obra (visitas mensais)', quantidade: 1, preco_unitario: 1200, desconto_percent: 0 },
    ],
    validade_dias_default: 15
  },
  {
    id: 'construcao-reforma',
    design_estilo: 'faixa',
    nome: 'Construção & Reforma',
    descricao: 'Orçamento detalhado de mão de obra, materiais e gestão de obra.',
    categoria: 'construcao',
    layout: { tema_cor: '#CA8A04', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Transparência e segurança em cada etapa da obra.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, segue orçamento detalhado para a obra/reforma de {{lead.nome}}, com prazos e materiais especificados para sua segurança e transparência.</p>',
      extra: { titulo: 'Escopo da obra', conteudo_html: '<p>Inclui mão de obra especializada, gestão do canteiro e fiscalização técnica durante toda a execução, seguindo as normas técnicas vigentes.</p>' },
      formaPagamento: '<p>Pagamento por etapa concluída (medição), com adiantamento de 20% para início da mobilização.</p>',
      observacoes: '<p>Valores sujeitos a reajuste caso haja alteração de escopo solicitada durante a execução.</p>'
    },
    itens_padrao: [
      { descricao: 'Mão de obra e execução', quantidade: 1, preco_unitario: 15000, desconto_percent: 0 },
      { descricao: 'Materiais de construção', quantidade: 1, preco_unitario: 12000, desconto_percent: 0 },
      { descricao: 'Gestão e fiscalização da obra', quantidade: 1, preco_unitario: 2000, desconto_percent: 0 },
    ],
    validade_dias_default: 10
  },
  {
    id: 'eventos-festas',
    design_estilo: 'blocado',
    nome: 'Eventos & Festas',
    descricao: 'Cerimonial, decoração e fornecedores para uma experiência inesquecível.',
    categoria: 'eventos',
    layout: { tema_cor: '#BE185D', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Cuidamos de cada detalhe para o seu grande dia.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}! Preparamos esta proposta especial para tornar {{lead.nome}} um evento inesquecível, cuidando de cada detalhe da experiência dos seus convidados.</p>',
      extra: { titulo: 'O que inclui', conteudo_html: '<ul><li>Cerimonial e coordenação no dia do evento</li><li>Decoração temática</li><li>Fornecedores parceiros (buffet, som e iluminação)</li><li>Cronograma detalhado do evento</li></ul>' },
      formaPagamento: '<p>Sinal de 30% para reserva de data, saldo dividido em até 3 parcelas até 15 dias antes do evento.</p>',
      observacoes: '<p>Buffet e bebidas orçados separadamente conforme número de convidados e cardápio escolhido.</p>'
    },
    itens_padrao: [
      { descricao: 'Cerimonial e coordenação', quantidade: 1, preco_unitario: 3500, desconto_percent: 0 },
      { descricao: 'Decoração completa', quantidade: 1, preco_unitario: 5000, desconto_percent: 0 },
      { descricao: 'Som e iluminação', quantidade: 1, preco_unitario: 2800, desconto_percent: 0 },
    ],
    validade_dias_default: 7
  },
  {
    id: 'saude-estetica',
    design_estilo: 'central',
    nome: 'Saúde & Estética',
    descricao: 'Plano de tratamento personalizado com acompanhamento profissional.',
    categoria: 'saude',
    layout: { tema_cor: '#0D9488', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Resultados seguros, com acompanhamento em todas as etapas.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, preparamos um plano de tratamento personalizado para {{lead.nome}}, com foco em resultados seguros e acompanhamento profissional em todas as etapas.</p>',
      extra: { titulo: 'Sobre o tratamento', conteudo_html: '<p>O protocolo é definido após avaliação individual, respeitando o histórico de saúde e as expectativas de cada paciente.</p>' },
      formaPagamento: '<p>Pagamento à vista com desconto ou parcelado em até 6x no cartão. Pacotes de sessões possuem condições especiais.</p>',
      observacoes: '<p>Resultados podem variar de pessoa para pessoa. Contraindicações serão avaliadas na consulta inicial.</p>'
    },
    itens_padrao: [
      { descricao: 'Avaliação e plano de tratamento', quantidade: 1, preco_unitario: 250, desconto_percent: 0 },
      { descricao: 'Sessões do protocolo indicado', quantidade: 4, preco_unitario: 450, desconto_percent: 0 },
      { descricao: 'Retorno de acompanhamento', quantidade: 1, preco_unitario: 0, desconto_percent: 0 },
    ],
    validade_dias_default: 15
  },
  {
    id: 'educacao-cursos',
    design_estilo: 'hero',
    nome: 'Educação & Cursos',
    descricao: 'Conteúdo programático, mentoria e certificado para cursos e treinamentos.',
    categoria: 'educacao',
    layout: { tema_cor: '#2563EB', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Aprendizado prático e aplicável desde a primeira aula.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, aqui está nossa proposta para o curso/treinamento de {{lead.nome}}, desenvolvido para gerar aprendizado prático e aplicável desde a primeira aula.</p>',
      extra: { titulo: 'Conteúdo programático', conteudo_html: '<ul><li>Módulos com aulas gravadas e ao vivo</li><li>Material de apoio e exercícios práticos</li><li>Certificado de conclusão</li><li>Suporte direto com os instrutores</li></ul>' },
      formaPagamento: '<p>Pagamento à vista com desconto ou parcelado em até 12x no cartão de crédito.</p>',
      observacoes: '<p>Acesso ao conteúdo liberado em até 24h após a confirmação do pagamento.</p>'
    },
    itens_padrao: [
      { descricao: 'Acesso ao curso completo', quantidade: 1, preco_unitario: 1200, desconto_percent: 0 },
      { descricao: 'Mentoria em grupo (mensal)', quantidade: 1, preco_unitario: 300, desconto_percent: 0 },
      { descricao: 'Certificado de conclusão', quantidade: 1, preco_unitario: 0, desconto_percent: 0 },
    ],
    validade_dias_default: 7
  },
  {
    id: 'imobiliaria',
    design_estilo: 'geometrico',
    nome: 'Imobiliária',
    descricao: 'Intermediação, avaliação e assessoria contratual para negociação de imóveis.',
    categoria: 'imobiliaria',
    layout: { tema_cor: '#059669', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Atendimento dedicado do início da negociação até a assinatura.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, segue nossa proposta de intermediação imobiliária para {{lead.nome}}, com atendimento dedicado do início da negociação até a assinatura do contrato.</p>',
      extra: { titulo: 'Serviços inclusos', conteudo_html: '<ul><li>Avaliação e divulgação do imóvel</li><li>Acompanhamento de visitas</li><li>Negociação e assessoria contratual</li><li>Suporte na documentação</li></ul>' },
      formaPagamento: '<p>Comissão de intermediação paga na assinatura do contrato, conforme percentual acordado sobre o valor da transação.</p>',
      observacoes: '<p>Documentação do imóvel deve estar regularizada para dar andamento à negociação.</p>'
    },
    itens_padrao: [
      { descricao: 'Taxa de intermediação (conforme tabela)', quantidade: 1, preco_unitario: 0, desconto_percent: 0 },
      { descricao: 'Assessoria documental', quantidade: 1, preco_unitario: 800, desconto_percent: 0 },
      { descricao: 'Suporte jurídico no fechamento', quantidade: 1, preco_unitario: 600, desconto_percent: 0 },
    ],
    validade_dias_default: 15
  },
  {
    id: 'servicos-tecnicos',
    design_estilo: 'geometrico',
    nome: 'Serviços Técnicos & Manutenção',
    descricao: 'Diagnóstico, mão de obra especializada e peças com garantia de serviço.',
    categoria: 'servicos_tecnicos',
    layout: { tema_cor: '#475569', cabecalho: { mostrar_logo: true, mostrar_dados_empresa: true, alinhamento: 'left' }, empresa: {}, rodape_texto: 'Orçamento sem compromisso, com garantia sobre o serviço executado.' },
    conteudo: {
      apresentacao: '<p>Olá {{contato.nome}}, segue orçamento técnico para o serviço solicitado por {{lead.nome}}, com peças originais/homologadas e garantia sobre o serviço executado.</p>',
      extra: { titulo: 'Detalhes do serviço', conteudo_html: '<p>O atendimento inclui diagnóstico técnico, execução do serviço e testes finais de funcionamento antes da entrega.</p>' },
      formaPagamento: '<p>Pagamento na conclusão do serviço, via PIX, cartão ou dinheiro. Orçamento sem compromisso.</p>',
      observacoes: '<p>Garantia de 90 dias sobre o serviço executado, não cobrindo mau uso ou danos posteriores.</p>'
    },
    itens_padrao: [
      { descricao: 'Visita técnica e diagnóstico', quantidade: 1, preco_unitario: 150, desconto_percent: 0 },
      { descricao: 'Mão de obra especializada', quantidade: 1, preco_unitario: 400, desconto_percent: 0 },
      { descricao: 'Peças e materiais', quantidade: 1, preco_unitario: 350, desconto_percent: 0 },
    ],
    validade_dias_default: 7
  },
];

export const MODELOS_PRE_PRONTOS = BASE_MODELOS.map(m => ({
  ...m,
  blocos: blocosPadrao(m.conteudo),
  modo_design: 'canvas',
  canvas: montarCanvasModelo(m),
}));

// Retorna o modelo com um canvas RECÉM-construído — cada proposta recebe sua própria
// cópia dos elementos (nunca compartilha referências mutáveis do catálogo estático).
export function getModeloPreProntoById(id) {
  const modelo = MODELOS_PRE_PRONTOS.find(m => m.id === id);
  return modelo ? { ...modelo, canvas: montarCanvasModelo(modelo) } : undefined;
}
